import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  ChartBarIcon, 
  ShoppingBagIcon, 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const Reports = () => {
  const [dateRange, setDateRange] = useState('month');

  const { data: stats, isLoading } = useQuery(
    ['vendor-stats', dateRange],
    () => api.get('/vendors/dashboard/stats').then(res => res.data.data)
  );

  const { data: ordersData } = useQuery(
    ['vendor-orders-report', dateRange],
    () => api.get('/orders/vendor/orders').then(res => res.data.data)
  );

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  const orders = ordersData?.orders || [];
  
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const completedOrders = orders.filter(o => o.status === 'delivered').length;
  
  const productSales = {};
  orders.forEach(order => {
    order.items?.forEach(item => {
      const productId = item.product_id;
      if (!productSales[productId]) {
        productSales[productId] = {
          name: item.product?.name || 'منتج',
          quantity: 0,
          revenue: 0
        };
      }
      productSales[productId].quantity += item.quantity;
      productSales[productId].revenue += parseFloat(item.total || 0);
    });
  });

  const topProducts = Object.values(productSales)
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'معلق',
      processing: 'قيد المعالجة',
      shipped: 'مشحون',
      delivered: 'مسلم',
      cancelled: 'ملغي'
    };
    return labels[status] || status;
  };

  return (
    <VendorLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">التقارير والإحصائيات</h1>
          
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="week">آخر أسبوع</option>
              <option value="month">آخر شهر</option>
              <option value="year">آخر سنة</option>
              <option value="all">كل الفترة</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الطلبات</p>
                <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <ShoppingBagIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الإيرادات</p>
                <p className="text-2xl font-bold text-gray-900">{totalRevenue.toLocaleString()} جنيه</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">طلبات معلقة</p>
                <p className="text-2xl font-bold text-gray-900">{pendingOrders}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">طلبات مكتملة</p>
                <p className="text-2xl font-bold text-gray-900">{completedOrders}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <ArrowTrendingUpIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold flex items-center">
              <ChartBarIcon className="w-5 h-5 ml-2 text-primary-600" />
              المنتجات الأكثر مبيعاً
            </h2>
          </div>
          <div className="p-6">
            {topProducts.length > 0 ? (
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-600">تم بيع {product.quantity} قطعة</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-primary-600">{product.revenue.toLocaleString()} جنيه</p>
                      <p className="text-sm text-gray-600">إجمالي الإيرادات</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ChartBarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>لا توجد بيانات مبيعات بعد</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold">الطلبات حسب الحالة</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700 mb-1">معلق</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {orders.filter(o => o.status === 'pending').length}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  {totalOrders > 0 ? ((orders.filter(o => o.status === 'pending').length / totalOrders) * 100).toFixed(1) : 0}%
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700 mb-1">قيد المعالجة</p>
                <p className="text-2xl font-bold text-blue-900">
                  {orders.filter(o => o.status === 'processing').length}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {totalOrders > 0 ? ((orders.filter(o => o.status === 'processing').length / totalOrders) * 100).toFixed(1) : 0}%
                </p>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700 mb-1">مشحون</p>
                <p className="text-2xl font-bold text-purple-900">
                  {orders.filter(o => o.status === 'shipped').length}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {totalOrders > 0 ? ((orders.filter(o => o.status === 'shipped').length / totalOrders) * 100).toFixed(1) : 0}%
                </p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 mb-1">مسلم</p>
                <p className="text-2xl font-bold text-green-900">
                  {orders.filter(o => o.status === 'delivered').length}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {totalOrders > 0 ? ((orders.filter(o => o.status === 'delivered').length / totalOrders) * 100).toFixed(1) : 0}%
                </p>
              </div>

              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700 mb-1">ملغي</p>
                <p className="text-2xl font-bold text-red-900">
                  {orders.filter(o => o.status === 'cancelled').length}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {totalOrders > 0 ? ((orders.filter(o => o.status === 'cancelled').length / totalOrders) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold">ملخص الطلبات الأخيرة</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">رقم الطلب</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">التاريخ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">العميل</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">المبلغ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.slice(0, 10).map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{order.order_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(order.created_at).toLocaleDateString('ar-SA')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {order.customer?.name || order.shipping_address?.name || 'عميل مباشر'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {parseFloat(order.total_amount).toLocaleString()} جنيه
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <ShoppingBagIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>لا توجد طلبات بعد</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default Reports;
